import { ReactNode } from 'react'
import { Button, ButtonLoading, ModalHeader } from '@tovala/component-library'
import {
  useChangeSubscriptionStatus,
  UserV1,
} from '@tovala/browser-apis-combinedapi'
import { DATE_FORMATS, formatDate } from 'utils/dates'
import { ErrorCodeMessageMapCombinedAPI } from 'types/internal'
import { isCombinedAPIResponseError } from 'utils/api'

import { useScheduledDelivery } from 'hooks/orderHistory'
import { useUserTermStatuses } from 'hooks/terms'
import { useUser } from 'contexts/user'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import Modal, { ModalPanel } from 'components/common/Modal'
import { track } from 'utils/analytics'
import { events } from 'analytics/events'

const CHANGE_SUBSCRIPTION_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't update your subscription due to a technical issue on our end.",
  },
}

const CancelMealPlanModal = ({
  activePromoCount,
  children,
  heading = '',
  onClickPauseDeliveries,
  onCloseModal,
  onCancelSuccess,
}: {
  activePromoCount: number
  children: ReactNode
  heading?: string
  onClickPauseDeliveries?: () => void
  onCloseModal(): void
  onCancelSuccess(): void
}) => {
  const { user } = useUser()

  const {
    error: changeSubscriptionStatusError,
    isError: hasChangeSubscriptionStatusError,
    isPending: isChangingSubscriptionStatus,
    mutate: changeSubscriptionStatus,
  } = useChangeSubscriptionStatus({
    onError: (err) => {
      /*
        If subscription is already canceled (via Glaze, for example), our user queries
        will be refreshed, and we'll show the user a success message
      */
      if (
        isCombinedAPIResponseError(err) &&
        err.response?.data.message ===
          'UnableToUpdateSubscriptionToExistingState'
      ) {
        onCancelSuccess()
      }
    },
    onSuccess: () => {
      track(events.CANCELS_MEAL_PLAN, {
        count_active_promos: activePromoCount,
      })
      onCancelSuccess()
    },
  })

  const handlePlanChange = () => {
    if (!user.subscription.subscriptionType) {
      return
    }

    changeSubscriptionStatus({
      data: {
        defaultShipPeriod: user.subscription.defaultShipPeriod,
        subscriptionTypeID: user.subscription.subscriptionType.id,
      },
      subscriptionStatus: 'cancel',
      userID: user.id,
    })
  }

  return (
    <Modal
      onCloseModal={() => {
        onCloseModal()
      }}
    >
      <ModalPanel>
        <div className="flex h-full max-h-[777px] w-[517px] flex-col md:max-h-full md:w-full">
          <ModalHeader onClickClose={onCloseModal}>
            {heading && <span className="whitespace-pre-wrap">{heading}</span>}
          </ModalHeader>

          <div className="flex-1 overflow-y-auto p-6 md:p-4">{children}</div>

          <div className="flex-shrink-0 space-y-4 p-6 md:p-4">
            {hasChangeSubscriptionStatusError && (
              <APIErrorDisplay
                error={changeSubscriptionStatusError}
                errorCodeMessageMap={CHANGE_SUBSCRIPTION_ERRORS}
              />
            )}

            <div className="grid grid-cols-1 gap-3">
              <ButtonLoading
                buttonStyle="dark"
                isLoading={isChangingSubscriptionStatus}
                onClick={() => {
                  handlePlanChange()
                }}
                size="large"
              >
                <span className="capitalize">Confirm cancel</span>
              </ButtonLoading>

              {onClickPauseDeliveries && (
                <Button
                  buttonStyle="stroke"
                  onClick={() => {
                    onClickPauseDeliveries()
                  }}
                  size="large"
                  type="submit"
                >
                  Pause deliveries
                </Button>
              )}
            </div>
          </div>
        </div>
      </ModalPanel>
    </Modal>
  )
}

export default CancelMealPlanModal

export const CancelBody = ({ user }: { user: UserV1 }) => {
  const { scheduledDelivery } = useScheduledDelivery({ userID: user.id })

  const { data: termStatuses } = useUserTermStatuses({ user })

  const presentUserTerm = termStatuses ? termStatuses[0] : ''
  const isOnBreak = termStatuses?.some((term) => term.isOnBreak)
  return (
    <div className="flex flex-col items-center py-10">
      <p className="mx-auto mb-6 max-w-sm text-body-lg">
        {isOnBreak ? (
          'This will cancel all future meal deliveries. You can restart your meal plan at any time.'
        ) : (
          <span>
            This will cancel your meals that were set to arrive{' '}
            {presentUserTerm ? (
              <span>
                the week of{' '}
                <strong>
                  {formatDate(presentUserTerm.startDate, {
                    format: DATE_FORMATS.DOW_MONTH_ABBR_DAY,
                  })}
                </strong>
              </span>
            ) : (
              'next'
            )}
            , as well as all future meal deliveries.{' '}
          </span>
        )}
        {scheduledDelivery && (
          <span>
            If you have a delivery scheduled for{' '}
            <strong>
              {formatDate(scheduledDelivery.userTermOrderSummary.deliveryDate, {
                format: DATE_FORMATS.DOW_MONTH_ABBR_DAY,
              })}
            </strong>
            , it will still be on its way.
          </span>
        )}
      </p>
    </div>
  )
}

export const CancelWithIncentivesBody = ({
  incentives,
  isOnBreak,
}: {
  incentives: { title: string; subtitle: string }[]
  isOnBreak: boolean
}) => {
  return (
    <div className="space-y-8">
      <div className="flex flex-col-reverse gap-8 text-center md:flex-col md:text-left">
        <div className="mx-auto h-[245px] w-[345px] overflow-hidden rounded-xl md:h-auto md:w-full">
          <img
            alt=""
            className="h-full w-full object-cover object-center"
            src="https://cdn.tovala.com/mytovala/images/cancel_modal_promo.png"
          />
        </div>

        <div className="mx-auto max-w-[469px] md:max-w-full">
          <h1 className="mb-4 text-k/32_105 font-semibold md:text-k/28_110 md:font-normal">
            {isOnBreak
              ? 'You lose your promo pricing when you cancel.'
              : 'Keep your promo pricing when you pause.'}
          </h1>
          <p className="mx-auto max-w-[345px] text-k/14_120 text-grey-8 md:max-w-full">
            {isOnBreak
              ? 'You can skip any weeks that you do not want to get food.'
              : 'If you need a break, you can skip your weekly deliveries for up to 8 weeks.'}
          </p>
        </div>
      </div>

      <div>
        <p className="mb-3 text-k/14_120 uppercase">Active Promotions</p>
        <div className="space-y-2">
          {incentives.map((incentive) => (
            <IncentiveCard
              key={incentive.title}
              subtitle={incentive.subtitle}
              title={incentive.title}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const IncentiveCard = ({
  subtitle,
  title,
}: {
  subtitle: string
  title: string
}) => {
  return (
    <div className="rounded-xl bg-[#E3EAEE] p-4">
      <div>
        <h2 className="text-k/20_110 font-semibold">{title}</h2>
        <p className="text-k/14_120 text-grey-10">{subtitle}</p>
      </div>
    </div>
  )
}
